"use client";

import {
  Category,
  Guess,
  MAXIMUM_MISTAKES,
  Puzzle,
  State,
} from "../models/gameModels.ts";
import { db, setDoc, UserData } from "./firebase.ts";
import { doc } from "@firebase/firestore/lite";
import { formatDate } from "../utils/dt-format.ts";
import { getTimer } from "./timer.ts";

export const getCachedState = (): State | undefined => {
  if (typeof window === "undefined") return;
  const state = localStorage.getItem(STATE_KEY);
  return state ? (JSON.parse(state) as State) : undefined;
};

export type UserState = {
  complete: Category[];
  incomplete: Category[];
  mistakesRemaining: number;
  result: null | "win" | "lose";
  guesses: Guess[];
  dt: number;
  items: string[];
  time?: number;
};

export const STATE_KEY = "puzzle-state";
export const cacheState = (state: State): void => {
  let stateToCache = { ...state };
  delete stateToCache["gridRef"];
  delete stateToCache["offline"];
  localStorage.setItem(STATE_KEY, JSON.stringify(stateToCache));
};
export const stateFromPuzzle = (puzzle: Puzzle): State => {
  return {
    incomplete: puzzle.categories,
    complete: [],
    items: puzzle.words,
    activeItems: [],
    transitioningItems: [],
    mistakesRemaining: MAXIMUM_MISTAKES,
    guesses: [],
    dt: Date.parse(puzzle.dt),
    gridRef: null,
    foundGroup: undefined,
    animationInProgress: "NONE",
    result: undefined,
    initialPuzzle: puzzle,
    offline: false,
  };
};

export const mergeUserState = (state: State, userState: UserState): State => {
  const { guesses, complete, incomplete, mistakesRemaining, result, items } =
    userState;
  return {
    ...state,
    guesses: [...guesses],
    complete: [...complete],
    incomplete: [...incomplete],
    mistakesRemaining,
    result: result ?? undefined,
    items: [...items],
  };
};

export const updateUserState = async (state: State, userData?: UserData) => {
  if (!userData) {
    return;
  }
  // dont sync state of puzzles not from today
  if (state.initialPuzzle.dt !== formatDate(new Date())) {
    return;
  }
  const { user, ...rest } = userData;
  const timer = getTimer();
  const userState: UserState = {
    mistakesRemaining: state.mistakesRemaining,
    result: state.result ?? null,
    complete: state.complete,
    incomplete: state.incomplete,
    guesses: state.guesses,
    dt: state.dt,
    items: state.items,
    time: timer?.date === state.initialPuzzle.dt ? timer.seconds : undefined,
  };
  await setDoc(
    doc(db, "users", userData.user.uid),
    {
      ...rest,
      state: userState,
    },
    {},
  );
};

export const clearUserState = async (userData?: UserData) => {
  if (!userData) {
    return;
  }
  const { user, ...rest } = userData;
  await setDoc(
    doc(db, "users", userData.user.uid),
    {
      ...rest,
      state: null,
    },
    {},
  );
};
